import Vector2 from '../math/Vector2';
import Renderer from './rendering/Renderer';
import Scene from './Scene';
import ArraySortable from './lists/ArraySortable';
import Touch from './Touch';
import { InteractionType } from './rendering/InteractionHandler';

/// #if DEVELOPMENT
import { debugRenderQueues } from './rendering/debugRender';
/// #endif DEVELOPMENT

const performance = window.performance || Date;
const cancelAnimationFrame = window.cancelAnimationFrame || window.clearTimeout;
const requestAnimationFrame = (function() {
	return  window.requestAnimationFrame ||
		function (cb) { return window.setTimeout(cb, 1000 / 60); };
})();

// interface Caches {
// 	[key: string]: Cache;
// }

type eventHandler = (event: any, touches: Array<Touch>) => void;
interface eventHandlersInterface {
	[handlerName: string]: Array<eventHandler>;
}


export default class Engine {
	private _requestAnimationFrameHandle: number;
	private scenes: ArraySortable;
	private _lastTick: number;
	private _eventHandlers: eventHandlersInterface;

	dt: number;
	dtMax: number;
	// caches: Caches;
	renderer: Renderer;
	// eventDisabled: boolean;

	constructor (engineOptions: any) {
		this._requestAnimationFrameHandle = 0;

		this.scenes = new ArraySortable();
		this.renderer = new Renderer(engineOptions.canvasOptions, engineOptions.contextOptions);

		this.dt = 0;
		this.dtMax = 5 * 16.7;
		this._lastTick = performance.now();

		// this.caches = {};
		// this.caches.textureCache = new Cache(128 * 1024 * 1024, function deleteTexture(texture) {
		// 	texture.delete();
		// });

		this._eventHandlers = {};

		// TODO: move out of engine constructor,
		// should be only once per session, not once per engine
		// special case for handling audio
		// also: should listen to document, not to canvas
		const initAudio = (event) => {
			// TODO:
			// initAudio();
			// console.error('TODO: init audio');
			this.removeListener('touchend', initAudio);
			this.removeListener('mousedown', initAudio);
		}
		this.addListener('touchend', initAudio);
		this.addListener('mousedown', initAudio);

		this.addListener('touchstart', (event, touches: Array<Touch>) => this.onInteraction('start', event, touches));
		this.addListener('mousedown', (event, touches: Array<Touch>) => this.onInteraction('start', event, touches));
		this.addListener('mouseup', (event, touches: Array<Touch>) => this.onInteraction('end', event, touches));
		this.addListener('touchend', (event, touches: Array<Touch>) => this.onInteraction('end', event, touches));
		this.addListener('touchmove', (event, touches: Array<Touch>) => this.onInteraction('move', event, touches));
		this.addListener('mousemove', (event, touches: Array<Touch>) => this.onInteraction('move', event, touches));
		this.addListener('touchcancel', (event, touches: Array<Touch>) => this.onInteraction('out', event, touches));
		this.addListener('mouseout', (event, touches: Array<Touch>) => this.onInteraction('out', event, touches));
		// this.addListener('mouseover', (event, touches: Array<Touch>) => this.onInteraction('over', event, touches));

		// starting the engine
		this.update();
	}

	private onInteraction (interactionType: InteractionType, event, touches: Array<Touch>) {
		for (var s = 0; s < this.scenes.length; s++) {
			this.scenes[s].onInteraction(interactionType, event, touches);
		}
	};

	addScene (scene: Scene, zIndex?: number) {
		if (zIndex !== undefined) {
			scene.zIndex = zIndex;
		}
		this.scenes.addSorted(scene);

		// this.addListener('touchstart', scene.onInputStart);
		// this.addListener('mousedown', scene.onInputStart);

		// this.addListener('touchmove', scene.onInputMove);
		// this.addListener('mousemove', scene.onInputMove);

		// this.addListener('touchend', scene.onInputEnd);
		// this.addListener('mouseup', scene.onInputEnd);
	}

	removeScene (scene: Scene) {
		this.scenes.remove(scene);

		// this.removeListener('touchstart', scene.onInputStart);
		// this.removeListener('mousedown', scene.onInputStart);

		// this.removeListener('touchmove', scene.onInputMove);
		// this.removeListener('mousemove', scene.onInputMove);

		// this.removeListener('touchend', scene.onInputEnd);
		// this.removeListener('mouseup', scene.onInputEnd);
	}

	// createScene (zIndex?: number, frameBuffer?: FrameBuffer) {
	// 	const scene = new Scene(frameBuffer || this.renderer.canvasFrameBuffer);
	// 	this.scenes.push(scene);
	// 	this.scenes.sort(function (a, b) {
	// 		return a.zIndex - b.zIndex;
	// 	});

	// 	return scene;
	// }

	createRenderTarget () {
		// TODO
	}

	resume () {
		this.stop();
		this.update();
	}

	stop () {
		cancelAnimationFrame(this._requestAnimationFrameHandle);
	}

	update () {
		const now = performance.now();
		this.dt = Math.max(0, Math.min((now - this._lastTick), this.dtMax));
		this._lastTick = now;

		this.renderer.reset();
		for (var s = 0; s < this.scenes.length; s++) {
			this.scenes[s].update(this.renderer, this.dt);
		}

		/// #if DEVELOPMENT
		let debugRenderQueue = debugRenderQueues[this.renderer.id];
		if (debugRenderQueue !== undefined) {
			while (debugRenderQueue.length > 0) {
				debugRenderQueue.shift()();
			}
		}
		/// #endif DEVELOPMENT

		this._requestAnimationFrameHandle = requestAnimationFrame(() => {
			this.update();
		});
	}

	private _getEventTouches (event) {
		const touches: Array<Touch> = [];
		if (event.changedTouches !== undefined) {
			const canvasPosition = this.renderer.canvas.getBoundingClientRect();
			const changedTouches = event.changedTouches;
			for (let t = 0; t < changedTouches.length; t++) {
				const touch = event.changedTouches[t];
				const x = touch.clientX - canvasPosition.left;
				const y = touch.clientY - canvasPosition.top;
				touches.push(new Touch(touch.identifier, new Vector2(x, y)));
			}
		}
		if (event.clientX !== undefined) {
			const canvasPosition = this.renderer.canvas.getBoundingClientRect();
			const x = event.clientX - canvasPosition.left;
			const y = event.clientY - canvasPosition.top;
			touches.push(new Touch(-1, new Vector2(x, y)));
		}
		return touches;
	}

	private _emitEvent (eventType: string, event) {
		const touches = this._getEventTouches(event);

		// make a copy of the listener array
		const handlers = this._eventHandlers[eventType].slice();
		for (let h = 0; h < handlers.length; h++) {
			handlers[h](event, touches);
		}
	}

	private _getEventEmitter (eventName: string) {
		// List of most used events:
		// touchstart
		// touchmove
		// touchend: fired when a touch point is removed from the touch surface.
		// touchcancel: fired when a touch point has been disrupted in an implementation-specific manner (for example, too many touch points are created).

		// mousedown
		// mousemove
		// mouseup

		// mouseout
		// mouseover

		// wheel

		// keydown
		// keypress
		// keyup

		// resize

		switch (eventName) {
			// TODO: make renderer propagate the resize event
			case 'resize':
				return window;
			case 'keydown':
			case 'keypress':
			case 'keyup':
				return document;
			default:
				return this.renderer.canvas;
		}
	}

	addListener (eventName: string, handler: eventHandler) {
		let handlers = this._eventHandlers[eventName];
		if (handlers === undefined) {
			this._eventHandlers[eventName] = handlers = [];
			const eventEmitter = this._getEventEmitter(eventName);
			const isOnCanvas = eventEmitter === this.renderer.canvas;
			const options = isOnCanvas ? {} : { passive: false };

			eventEmitter.addEventListener(eventName, (event) => {
					if (isOnCanvas) {
						event.preventDefault();
						event.stopPropagation();
					}
					this._emitEvent(eventName, event);
				}, options);
		}
		handlers.push(handler);
	}

	removeListener (eventName: string, handler: eventHandler) {
		const handlers = this._eventHandlers[eventName];
		const index = handlers.indexOf(handler);
		if (index !== -1) {
			handlers.splice(index, 1);
		}
		// TODO: enable in development
		// else {
		// 	return console.error('[removeEventListener] cannot find registered handler for event ' + eventName);
		// }
	}
}
